/**
 * What the team tabs SAY when leader/member exclusivity changes an edit under the user.
 *
 * The rule itself belongs to the domain and lives on the classes — `VendorTeam.isLedBy`,
 * `VendorTeam.membersDisplacedByLeader`, `VendorTeam.withLeaderRemovedFromMembers`. These are the
 * words the proposal team tabs put on screen when that rule bites, and words are a view concern: a
 * domain class should not be where snackbar copy is kept.
 */

/** Said when a template (or a member checkbox) tried to add the person who is already leading. */
export function getTeamLeaderCannotAlsoBeMemberMessage(personName?: string): string {
  return `${personName ?? "That person"} is already the team leader, so they were not also added as a team member.`;
}

/**
 * Said when the chosen leader belongs to no firm or company. A leader is seated as a person within
 * their firm, so someone outside one cannot lead, and the user has to choose somebody else (#6614).
 */
export function getLeaderWithoutFirmMessage(personName?: string): string {
  return `${personName ?? "That person"} can't lead the team because they don't belong to a firm or company. Choose another leader.`;
}

/** Said when a member has just been promoted, which takes them off the member list. */
export function getTeamMemberBecameLeaderMessage(personName?: string): string {
  return `${personName ?? "That person"} moved from team member to team leader.`;
}

/**
 * Said when the roster's one-click promotion swapped the two roles: the member now leads the team, and
 * the person who led it stays on the team as a member.
 *
 * The swap is not a confirm step (#5668 asks for one click), so it is reported after the fact. The
 * demotion is the half the user did not name, which is why the words say what became of that person
 * rather than only what became of the one who was pressed.
 */
export function getTeamLeaderSwappedWithMemberMessage(newLeaderName?: string, formerLeaderName?: string): string {
  return (
    `${newLeaderName ?? "That person"} is now the team leader. ` +
    `${formerLeaderName ?? "The previous leader"} is now a team member.`
  );
}

/**
 * The prompt shown before promoting a current member to leader. Seating them removes them from the
 * member list, which is a change the user did not ask for in the same click, so it is asked for
 * rather than done quietly — the same courtesy `getNewLeaderFromTemplate`'s "Replace leader?" prompt
 * extends when a template would displace a leader the user chose.
 */
export function getPromoteTeamMemberToLeaderPrompt(personName?: string): {
  title: string;
  message: string;
  okButtonText: string;
  cancelButtonText: string;
} {
  return {
    title: "Make team leader?",
    message:
      `${personName ?? "That person"} is currently a team member. A person cannot lead the team and ` +
      "belong to it at the same time, so they will be removed from the member list.",
    okButtonText: "Make Leader",
    cancelButtonText: "Cancel",
  };
}
